import React, { useState, useMemo } from 'react';

// --- Internal Component: Acknowledgment Modal ---
const PoshAcknowledgeModal = ({ onClose, onAcknowledge }) => {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [signature, setSignature] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const today = useMemo(() => new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }), []);

    // Allows all characters for a full signature
    const handleSignatureChange = (e) => {
        setSignature(e.target.value);
    };

    const handleFinalAcknowledge = () => {
        if (name && employeeId && signature && isConfirmed) {
            onAcknowledge({ name, employeeId, signature, date: today });
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center p-4 z-50 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-4xl w-full max-w-lg overflow-hidden transform transition-all scale-100 border-t-8 border-indigo-600">
                
                {/* Modal Header */}
                <div className="p-6 bg-indigo-50 flex justify-between items-center">
                    <h3 className="text-xl font-poppins font-bold text-gray-800">
                        Mandatory Policy Acknowledgment (Step {step} of 3)
                    </h3>
                    <button onClick={onClose} className="text-gray-600 hover:text-gray-900 transition">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6">
                    {step === 1 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-indigo-700">Declaration of Understanding</h4>
                            <p className="text-sm text-gray-600 leading-relaxed bg-indigo-100 p-4 rounded-lg border border-indigo-200">
                                I solemnly declare that I have accessed, read, and fully comprehend the company's Comprehensive Policy on the Prevention of Sexual Harassment, which adheres to the <span className="font-semibold">POSH Act, 2013, Government of India</span>. I agree to abide by this policy strictly.
                            </p>
                            <label className="flex items-center space-x-2 text-sm text-gray-700 pt-2">
                                <input 
                                    type="checkbox" 
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <span>I affirm that I understand and consent to the terms of this policy.</span>
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 font-roboto">
                            <h4 className="font-poppins font-semibold text-indigo-700">Identification Details</h4>
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Legal Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div>
                                <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">Employee ID</label>
                                <input
                                    type="text"
                                    id="employeeId"
                                    value={employeeId}
                                    onChange={(e) => setEmployeeId(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g., EMP12345"
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-indigo-700">Digital Signature</h4>
                            <div>
                                <label htmlFor="signature" className="block text-sm font-medium text-gray-700">Type Your Full Signature (Full Name)</label>
                                <input
                                    type="text"
                                    id="signature"
                                    value={signature}
                                    onChange={handleSignatureChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-lg p-4 text-xl tracking-wide focus:ring-indigo-500 focus:border-indigo-500 font-poppins font-semibold"
                                    placeholder="e.g., Varun Sharma"
                                />
                                <p className="mt-2 text-xs text-red-600">
                                    <span className="font-bold">Caution:</span> This electronic acknowledgment confirms mandatory legal compliance.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 bg-gray-50 flex justify-end space-x-3 border-t border-gray-200">
                    {step > 1 && (
                        <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition shadow-sm">
                            Back
                        </button>
                    )}
                    {step < 3 && (
                        <button 
                            onClick={() => setStep(step + 1)} 
                            disabled={(step === 1 && !isConfirmed) || (step === 2 && (!name || !employeeId))}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition shadow-md ${
                                ((step === 1 && !isConfirmed) || (step === 2 && (!name || !employeeId))) 
                                    ? 'bg-indigo-300 text-white cursor-not-allowed' 
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                        >
                            Continue
                        </button>
                    )}
                    {step === 3 && (
                        <button 
                            onClick={handleFinalAcknowledge} 
                            disabled={signature.length === 0}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition shadow-md ${
                                signature.length === 0
                                    ? 'bg-green-300 text-white cursor-not-allowed' 
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            Acknowledge & Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Content Section Helper ---
const PolicySection = ({ id, title, children }) => (
    <section id={id} className="mb-12 border-b border-gray-200 pb-8">
        <h2 className="text-3xl font-poppins font-bold text-gray-900 mb-6 border-l-4 border-indigo-600 pl-4 py-1 bg-indigo-50 rounded-lg">
            {title}
        </h2>
        <div className="space-y-6">
            {children}
        </div>
    </section>
);


// --- Main App Component ---
const App = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [acknowledgment, setAcknowledgment] = useState(null);

    // Data structure for the navigation and content mapping
    const tabs = useMemo(() => [
        { id: 'policy', title: '1. Definition & Scope'},
        { id: 'ic', title: '2. Internal Committee (IC)'},
        { id: 'procedure', title: '3. Complaint & Inquiry'},
        { id: 'preventive', title: '4. Preventive Measures'},
        { id: 'duties', title: '5. Employer Duties'},
        { id: 'penalty', title: '6. Penalties & Protection'},
    ], []);

    const baseClasses = "text-base leading-relaxed text-gray-700 font-roboto";
    const ulClasses = "list-disc ml-6 space-y-4 pt-2 font-roboto";
    const liClasses = "p-3 bg-gray-50 rounded-lg border border-gray-100 shadow-sm";

    // Content definitions for each section
    const contentMap = {
        policy: (
            <div className="space-y-6">
                <div className="p-4 bg-indigo-50 border-l-4 border-indigo-600 rounded-lg shadow-sm">
                    <h3 className="font-poppins font-semibold text-indigo-700 mb-1">Definition:</h3>
                    <p className="text-sm text-gray-700 font-roboto">
                        Sexual harassment includes any unwelcome act or behavior (physical, verbal, non-verbal, or electronic) of a sexual nature. The test is the impact on the Aggrieved Woman, not the intention of the Respondent.
                    </p>
                </div>

                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Forms of Harassment (Quid Pro Quo & Hostile Environment)</h3>
                <ul className={ulClasses}>
                    <li className={liClasses}>
                        <span className="font-semibold text-indigo-700">Quid Pro Quo (This for That):</span> Explicit or implicit promise of preferential/detrimental treatment in employment in exchange for sexual favors.
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-indigo-700">Hostile Work Environment:</span> Creating an intimidating, offensive, or humiliating work environment, or interfering with an employee's work performance.
                    </li>
                </ul>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Extended Workplace Scope</h3>
                <p className={baseClasses}>
                    The policy covers all employees and applies across the extended definition of "workplace" which includes:
                </p>
                <ul className="list-disc ml-6 space-y-2 pt-2 text-sm text-gray-600">
                    <li>Any place visited by the employee arising out of or during the course of employment, including transportation.</li>
                    <li>Electronic communication platforms (emails, chat groups, video calls).</li>
                    <li>Company-sponsored social or recreational events.</li>
                </ul>
            </div>
        ),
        ic: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Constitution Requirements</h3>
                <div className="bg-blue-50 p-4 rounded-lg shadow-md border-l-4 border-blue-600 font-roboto">
                    <ul className="list-disc ml-4 space-y-3 text-gray-700">
                        <li>Presiding Officer (a woman employed at a senior level).</li>
                        <li>Not less than two employees committed to the cause of women or having legal knowledge.</li>
                        <li>One External Member from an NGO or familiar with POSH issues.</li>
                        <li><span className="font-semibold text-blue-700">Mandate:</span> Minimum fifty percent (1/2) of the IC members shall be women.</li>
                    </ul>
                </div>

                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Powers of the Internal Committee</h3>
                <p className={baseClasses}>
                    For the purpose of making an inquiry, the Internal Committee is vested with the same powers as a civil court under the Code of Civil Procedure, 1908, in respect of:
                </p>
                <ul className={ulClasses}>
                    <li className={liClasses}>Summoning and enforcing the attendance of any person and examining him on oath.</li>
                    <li className={liClasses}>Requiring the discovery and production of documents.</li>
                    <li className={liClasses}>Any other matter which may be prescribed.</li>
                </ul>
            </div>
        ),
        procedure: (
            <div className="space-y-6">
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-600 rounded-lg shadow-sm">
                    <h3 className="font-poppins font-semibold text-yellow-800 mb-1">Filing Timeline:</h3>
                    <p className="text-base text-gray-700 font-roboto">
                        A written complaint must be submitted within <span className="font-extrabold text-yellow-900">three months</span> of the incident. The IC may extend this period by a further three months if the circumstances prevent the filing in time.
                    </p>
                </div>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Key Stages of Inquiry</h3>
                <ul className="space-y-5 pt-2">
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-indigo-400">
                        <span className="font-extrabold text-indigo-600 text-lg mr-2">I. Conciliation:</span> If requested by the Aggrieved Woman, the IC can attempt conciliation before inquiry begins. <span className="font-bold text-red-600">No monetary settlement is permitted.</span>
                    </li>
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-indigo-400">
                        <span className="font-extrabold text-indigo-600 text-lg mr-2">II. Inquiry Initiation:</span> The IC serves the Respondent with a copy of the complaint within 7 working days. The Respondent must file a response within 10 working days.
                    </li>
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-indigo-400">
                        <span className="font-extrabold text-indigo-600 text-lg mr-2">III. Interim Relief:</span> During inquiry, the IC may recommend transferring the Aggrieved Woman or Respondent, granting leave (up to 3 months), or restraining the Respondent from contacting the Complainant.
                    </li>
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-indigo-400">
                        <span className="font-extrabold text-indigo-600 text-lg mr-2">IV. Final Report:</span> The inquiry must be completed within 90 days. The IC must submit its report to the Employer within 10 days of completion.
                    </li>
                </ul>
            </div>
        ),
        preventive: (
            <div className="space-y-6">
                <p className={baseClasses}>
                    The policy emphasizes a proactive approach to preventing harassment through continuous training, sensitization, and clear communication.
                </p>

                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Mandatory Awareness Initiatives</h3>
                <ul className={ulClasses}>
                    <li className={liClasses}>Organizing workshops and sensitization programs at regular intervals (minimum once a year) for all employees.</li>
                    <li className={liClasses}>Displaying the penal consequences of sexual harassment and the order constituting the IC at a conspicuous place in the workplace.</li>
                    <li className={liClasses}>Ensuring regular orientation and training for the IC members on inquiry procedures and legal amendments.</li>
                </ul>

                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Internal Guidelines</h3>
                <div className="bg-indigo-50 p-4 rounded-lg shadow-md border-l-4 border-indigo-600 font-roboto">
                    <ul className="list-disc ml-4 space-y-3 text-gray-700 text-sm">
                        <li>Clear communication regarding zero-tolerance for retaliation against anyone who files a complaint or participates in an inquiry.</li>
                        <li>Integration of POSH policy compliance into employee appraisals and management performance indicators.</li>
                    </ul>
                </div>
            </div>
        ),
        duties: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Statutory Obligations</h3>
                <p className={baseClasses}>
                    The Employer is legally responsible for ensuring a safe working environment and executing the recommendations of the Internal Committee. These duties include:
                </p>
                <ul className={ulClasses}>
                    <li className={liClasses}>Providing necessary facilities to the IC for dealing with the complaint and conducting the inquiry.</li>
                    <li className={liClasses}>Causing to initiate action against the Respondent as per the recommendations of the IC within <span className="font-bold">sixty days</span> of receipt of the report.</li>
                    <li className={liClasses}>Monitoring the timely submission of reports by the IC (Annual Report).</li>
                    <li className={liClasses}>Assisting the Aggrieved Woman if she chooses to file a police complaint (under IPC, 1860) in relation to the incident.</li>
                </ul>
            </div>
        ),
        penalty: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Disciplinary Action Against Respondent</h3>
                <p className={baseClasses}>
                    If the IC finds the complaint substantiated, disciplinary action, based on the severity of the offense and company service rules, may include:
                </p>
                <ul className={ulClasses}>
                    <li className={liClasses}>Warning, written apology, censure, or reprimand.</li>
                    <li className={liClasses}>Withholding of promotion, salary increments, or bonus.</li>
                    <li className={liClasses}>Termination of employment.</li>
                </ul>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Penalty for Employer Non-Compliance</h3>
                <div className="bg-red-50 p-4 rounded-lg shadow-md border-l-4 border-red-600 font-roboto">
                    <h4 className="font-poppins font-semibold text-red-700 mb-2">Legal Liability:</h4>
                    <p className="text-sm text-gray-700">
                        Failure by the Employer to constitute an IC, or failure to comply with the directions of the Act, can result in a fine of up to <span className="font-extrabold">₹50,000</span>. Repeated non-compliance can lead to double the penalty and, potentially, cancellation of the business license/registration.
                    </p>
                </div>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Protection Against Victimization</h3>
                <p className={baseClasses}>
                    No adverse action, including transfer, denial of promotion, or termination, shall be taken against the Aggrieved Woman or any person assisting in the inquiry process (witnesses) due to their participation in proceedings.
                </p>
            </div>
        )
    };

    return (
        <>
            <style>
                {/* CSS and Font Definitions (kept outside JSX for cleaner syntax) */}
                {`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap');
                .font-poppins { font-family: 'Poppins', sans-serif; }
                .font-roboto { font-family: 'Roboto', sans-serif; }
                .shadow-4xl {
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
                }
                `}
            </style>
            
            {/* Minimal Main Container for Plain Content */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-roboto bg-white">
                
                {/* Header Section - Plain Style */}
                <header className="py-8 border-b border-gray-300 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div className="flex items-center mb-3 sm:mb-0">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 mr-4 text-indigo-700 p-1 bg-indigo-100 rounded-full flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2h-1c-1.657 0-3 .895-3 2s1.343 2 3 2h1a2 2 0 002-2v-3.586a1 1 0 00-.293-.707l-2-2a1 1 0 00-1.414 0l-2 2a1 1 0 00-.293.707V12a3 3 0 11-6 0v-2c0-1.105.895-2 2-2h1m-1 0a2 2 0 114 0h-4z"></path></svg>
                        <div>
                            <h1 className="text-xl sm:text-3xl font-poppins font-extrabold text-gray-900">
                                Comprehensive POSH Compliance Policy
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-500 font-roboto">
                                Prevention of Sexual Harassment at Workplace Act, 2013, India
                            </p>
                        </div>
                    </div>

                    {/* Acknowledge Button */}
                    {acknowledgment ? (
                        <div className="text-sm font-medium text-green-700 bg-green-100 p-2.5 rounded-xl border border-green-200 mt-4 sm:mt-0">
                            Acknowledged by: {acknowledgment.signature} on {acknowledgment.date}
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="w-full sm:w-auto mt-4 sm:mt-0 px-5 py-2.5 bg-indigo-600 text-white font-poppins font-semibold text-sm rounded-xl shadow-lg hover:bg-indigo-700 transition transform focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:ring-opacity-50"
                        >
                            Acknowledge Policy
                        </button>
                    )}
                </header>
                
                {/* Content Table of Contents/Navigation Links */}
                <nav className="my-6 sticky top-0 bg-white pt-2 pb-4 z-10 border-b border-gray-100">
                    <h3 className="text-lg font-poppins font-semibold text-gray-800 mb-3">Policy Sections:</h3>
                    <div className="flex flex-wrap gap-2">
                        {tabs.map(tab => (
                            <a 
                                key={tab.id} 
                                href={`#${tab.id}`}
                                className="px-4 py-2 text-sm font-medium rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition shadow-sm"
                            >
                                {tab.title}
                            </a>
                        ))}
                    </div>
                </nav>

                {/* Policy Content Area - Sequential Flow */}
                <main className="pt-4">
                    {Object.entries(contentMap).map(([id, content], index) => (
                        <PolicySection key={id} id={id} title={tabs[index].title}>
                            {content}
                        </PolicySection>
                    ))}
                </main>
            </div>

            {/* Conditional Modal Render */}
            {isModalOpen && <PoshAcknowledgeModal 
                onClose={() => setIsModalOpen(false)} 
                onAcknowledge={setAcknowledgment} 
            />}
        </>
    );
};

export default App;